'use client';

import { DUMMY_INTERFACE_RECORD } from '@unifyapps/carbon/no-code/const/dummy';
import { PAGE_FIELD_KEYS_TO_TRACK } from '@unifyapps/carbon/no-code/context/DependencyGraphProvider';
import { useNonReactiveComputeContext } from '@unifyapps/carbon/no-code/hooks/computeContext';
import { useGetShouldEvaluateResult } from '@unifyapps/carbon/no-code/hooks/computeContext/useGetShouldEvaluateResult';
import { getComputedData } from '@unifyapps/carbon/no-code/utils/getComputedData';
import { useSetDocumentTitle } from '@unifyapps/hooks/useSetDocumentTitle';
import { useTranslation } from '@unifyapps/i18n/client';
import { memo, useMemo } from 'react';
import { InterfaceModes } from '../../../stores/InterfaceStore';

export type DocumentTitleProps = {
  documentTitle?: string;
  mode?: InterfaceModes;
  appName?: string;
  name?: string;
  shouldSetDocumentTitle?: boolean;
};

export const DocumentTitle = memo(
  ({ documentTitle, mode, appName, name, shouldSetDocumentTitle = true }: DocumentTitleProps) => {
    const { t } = useTranslation('interfaces-matrix');
    const { context } = useNonReactiveComputeContext({
      entityId: PAGE_FIELD_KEYS_TO_TRACK.DOCUMENT_TITLE,
    });
    const shouldEvaluateResult = useGetShouldEvaluateResult();

    const computedDocumentTitle = useMemo(() => {
      if (!documentTitle) return '';
      return getComputedData(documentTitle, context, shouldEvaluateResult);
    }, [context, documentTitle, shouldEvaluateResult]);

    const title = useMemo(() => {
      const computedTitle = typeof computedDocumentTitle === 'string' ? computedDocumentTitle : '';
      const matrixFallbackTitle = t('interfaces-matrix:DocumentTitle.Fallback.Matrix');
      const pageFallbackTitle = t('interfaces-matrix:DocumentTitle.Fallback.Page');
      const applicationFallbackTitle = t('interfaces-matrix:DocumentTitle.Fallback.Application');

      if (mode === InterfaceModes.RUNNER) {
        return computedTitle || name || appName || matrixFallbackTitle;
      }

      const displayDocumentTitle = computedTitle || name || pageFallbackTitle;

      return appName
        ? `${displayDocumentTitle} | ${appName} | ${applicationFallbackTitle}`
        : `${displayDocumentTitle} | ${applicationFallbackTitle}`;
    }, [appName, computedDocumentTitle, name, mode, t]);

    const isDummyTitle = title === DUMMY_INTERFACE_RECORD.properties.name;

    useSetDocumentTitle(
      title,
      Boolean(title) && !process.env.DELTA_MATRIX && !isDummyTitle && shouldSetDocumentTitle,
    );

    return null;
  },
);

DocumentTitle.displayName = 'DocumentTitle';
